import type { ReactElement, SVGProps } from 'react';
import type { EmoteId } from '@il-colpo/shared';

/**
 * The pictures of the emotes (GDD §13.3), drawn from code like every other icon: a 24×24 grid, a 2 px
 * stroke, round caps. Each one is a small noir prop; none is decoration only, so the name of the
 * emote is the label of whatever holds it.
 */
const GLYPHS: Record<EmoteId, ReactElement> = {
  // A fedora lifted in greeting.
  hat: (
    <>
      <path d="M3 16.5h18" />
      <path d="M6.5 16.5c0-5 1.6-9.5 5.5-9.5s5.5 4.5 5.5 9.5" />
      <path d="M6.8 13.2h10.4" />
    </>
  ),
  thumbsup: (
    <>
      <path d="M3.5 11h3.5v9H3.5z" />
      <path d="M7 11l3.8-7c1.6 0 2.6 1.2 2.2 2.8L12.3 9.5H18a2 2 0 0 1 2 2.3l-1 6.2a2 2 0 0 1-2 1.8H7" />
    </>
  ),
  // A face that keeps a secret, a finger across the mouth.
  shh: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9 9.5v.01M15 9.5v.01" strokeWidth={3} />
      <path d="M12 11.5v7" strokeWidth={3} />
    </>
  ),
  sweat: (
    <>
      <path d="M12 3c3.5 4.5 6 7.5 6 11a6 6 0 0 1-12 0c0-3.5 2.5-6.5 6-11z" />
      <path d="M9.5 15a2.8 2.8 0 0 0 2 2.7" />
    </>
  ),
  cigar: (
    <>
      <rect x="3" y="12.5" width="15" height="4" rx="1.5" />
      <path d="M13.5 12.5v4" />
      <path d="M20.2 14.5v.01" strokeWidth={3} />
      <path d="M20 9.5c-1.8-1.3 1.3-2.7-.4-4.5" />
    </>
  ),
  watch: (
    <>
      <circle cx="12" cy="13.5" r="7" />
      <path d="M12 13.5V9.5M12 13.5l3 1.8" />
      <path d="M10 3.5h4M12 3.5v3" />
    </>
  ),
  glass: (
    <>
      <path d="M6 4h12l-1.5 6a4.5 4.5 0 0 1-9 0z" />
      <path d="M12 14.5V20M8.5 20h7" />
      <path d="M20 3v3M18.5 4.5h3" />
    </>
  ),
  magnifier: (
    <>
      <circle cx="10.5" cy="10.5" r="6" />
      <path d="M15 15l5.5 5.5" />
      <path d="M8 9a3 3 0 0 1 3-2.2" />
    </>
  ),
};

export function EmoteGlyph({
  id,
  size = '1.6em',
  ...rest
}: { id: EmoteId; size?: number | string } & Omit<SVGProps<SVGSVGElement>, 'children'>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {GLYPHS[id]}
    </svg>
  );
}
